import React from 'react';
import { useSelector } from 'src/client/redux';
import styled from 'styled-components/macro';
import { promoCategoriesConfig, PromoCategoryConfig } from 'src/shared/constants/promoCategoryConfig';
import { MOBILE_LANDSCAPE, TABLET_PORTRAIT } from 'src/client/constants/mediaQueries';
import { GalleryCategories } from '@tovia/man-protos/dist/types/content.types';
import { Site } from 'src/client/redux/modules/site';
import { useCookies } from 'react-cookie';
import { useJoinUrlGenerator } from '../buttons/JoinButton';
import { isPartner } from 'src/client/utils/affiliateTracking';
import { EternalDesire, RylskyArt } from 'src/client/constants/sites';
import { PhotographerUUIDS } from 'src/shared/constants/photographers';
import { createAffiliateURL, getAffiliateCodeForRedirect } from '@tovia/man-app-utils/lib/helpers/affiliate';
import { ensure } from 'src/client/utils/type-utils';
import { MAM_COOKIE_NAME, PARTNER_BANNER_COOKIE } from 'src/shared/constants/cookies';

type Props = {
  categories?: GalleryCategories[];
  setPromoPhotographer?: string;
  site?: Site | null;
  setType?: string;
};

export const PartnerInlineBanner = (props: Props) => {
  const categoryConfig = usePromoCategory(props);
  const cdnUrl = useSelector((state) => state.app.cdnUrl);

  if (!categoryConfig.showBanner) {
    return null;
  }

  return (
    <BannerWrapper>
      <BannerLink
        assetsAspectRatios={categoryConfig.assetsAspectRatios}
        assetsDirectoryPath={`${cdnUrl}/promoBanners/${categoryConfig.assetsDirectory}`}
        href={categoryConfig.url}
        target={categoryConfig.openUrlInNewTab ? '_blank' : '_self'}
      />
    </BannerWrapper>
  );
};

export const usePromoCategory = (props: Props) => {
  const { categories, site, setPromoPhotographer } = props;
  const { user } = useSelector((state) => state.auth);
  const [cookies] = useCookies([MAM_COOKIE_NAME, PARTNER_BANNER_COOKIE]);
  const generateJoinUrl = useJoinUrlGenerator();
  const { accessSubdomain, siteSubdomain, config } = useSelector((state) => state.app);
  const sites = useSelector((state) => state.sites.items);
  const promoPhotographer = promoCategoriesConfig.categories.find(
    (photographer) => photographer.UUID === setPromoPhotographer,
  );

  const { partnerBannersEnabled = [] } = config;

  let categoryConfig = promoCategoriesConfig.getCategoryConfig(categories);
  let showBanner = false;
  let url = categoryConfig?.url;

  if (categoryConfig) {
    // if we don't have an external URL then we are going to assume that they should be sent to OUR join pages
    // down below this means that we will not open the URL in a new tab
    if (!categoryConfig.url) {
      url = generateJoinUrl({ campaign: categoryConfig.categoryName });
    }

    // Two different categories of users here since each promo category might have different targets:
    //   1. Guests and guest members (users without active subscriptions) that came through a MAM link
    //   2. Members with active subscriptions
    const isAbleToSeeBanners = categoryConfig.whitelistedPartnerIDs
      ? isPartner(cookies[MAM_COOKIE_NAME], categoryConfig.whitelistedPartnerIDs) && !user?.validSubscription
      : user?.validSubscription;

    const isEnabledForSite = categoryConfig.sitesToDisplay?.includes(site?.abbreviation || '');

    showBanner = Boolean(isAbleToSeeBanners && isEnabledForSite);

    // If we are not having a promotion display that is based on the categories of a set, but we want to display a banner based on the
    // photographer, we go this way
  } else if (!categoryConfig && promoPhotographer?.sitesToHide) {
    const isAbleToSeeBanners =
      user?.validSubscription && !promoPhotographer?.sitesToHide.includes(site?.abbreviation || '');
    const abbreviationToFind =
      promoPhotographer.UUID === PhotographerUUIDS.Rylsky ? RylskyArt.abbreviation : EternalDesire.abbreviation;
    const redirectSite = ensure(sites.find((site) => site?.abbreviation === abbreviationToFind));

    const affiliateCode = getAffiliateCodeForRedirect(user?.firstPurchase, redirectSite.UUID);
    url = createAffiliateURL({
      accessSubdomain,
      affiliateCode,
      siteDomain: redirectSite.domain.toLowerCase(),
      path: user ? 'auth/man' : undefined,
      siteSubdomain,
    });

    categoryConfig = promoPhotographer;
    showBanner = Boolean(isAbleToSeeBanners);

    // Finally if there's no special partner banner to show, we will check the
    // cookie to see which of the enabled banners to show.
  } else if (user?.validSubscription) {
    const categoryName = partnerBannersEnabled[cookies[PARTNER_BANNER_COOKIE] - 1] || '';
    const urlSearchParams = new URLSearchParams();

    if (categoryName === 'goddessme-promo') {
      urlSearchParams.set('utm_medium', 'web');
      urlSearchParams.set('utm_source', props?.site?.abbreviation || '');
      urlSearchParams.set('utm_campaign', props.setType || '');
    }

    const category = promoCategoriesConfig.categories?.find((category) => category.categoryName === categoryName);

    if (category) {
      categoryConfig = category;
      showBanner = true;

      // assuming that in this code block we should always have a URL in the category config.
      // the current banner options are for external partner promos only
      const categoryURL = new URL(category.url as string);

      for (const [key, value] of urlSearchParams.entries()) {
        categoryURL.searchParams.set(key, value);
      }

      url = categoryURL.toString();
    }
  }

  return {
    ...categoryConfig,
    openUrlInNewTab: Boolean(categoryConfig?.url) || promoPhotographer, // only open in new tab when it's an external URL from the config or if we are promoting a photographer
    showBanner,
    url,
  };
};

const BannerWrapper = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px 0;
`;

const BannerLink = styled.a<{
  assetsAspectRatios?: PromoCategoryConfig['assetsAspectRatios'];
  assetsDirectoryPath: string;
}>`
  aspect-ratio: ${(props) => props.assetsAspectRatios?.mobile_ver};
  display: block;
  cursor: pointer;
  background-repeat: no-repeat;
  background-image: url(${(props) => `${props.assetsDirectoryPath}/mobile_ver.jpg`});
  background-position: center;
  background-size: contain;
  max-width: 346px;
  width: 100%;

  @media ${MOBILE_LANDSCAPE} {
    @media (orientation: landscape) {
      aspect-ratio: ${(props) => props.assetsAspectRatios?.mobile_hor};
      background-image: url(${(props) => `${props.assetsDirectoryPath}/mobile_hor.jpg`});
      max-width: 620px;
    }
  }

  @media ${TABLET_PORTRAIT} {
    aspect-ratio: ${(props) => props.assetsAspectRatios?.desktop};
    background-image: url(${(props) => `${props.assetsDirectoryPath}/desktop.jpg`});
    max-width: 959px;
  }
`;
